import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useT, LanguageSwitcher } from "./i18n";

// ────────────────────────────────────────────────────────────────────────────
// Asset URLs (from design_guidelines.json)
// ────────────────────────────────────────────────────────────────────────────
export const ASSETS = {
  logo: "https://static.prod-images.emergentagent.com/jobs/37ba2cd9-8a17-4c96-92e9-5049dd831d49/images/6033c0e3929c477da8390dd3034465458342da47ea8925eac8d800c667ff1e9d.png",
  heroCard:
    "https://static.prod-images.emergentagent.com/jobs/37ba2cd9-8a17-4c96-92e9-5049dd831d49/images/ac6001b774d8a0bd2c6d8f2fa9c4b8830b1907296adb3abfc8f32d73a7ade9e8.png",
  darkTexture:
    "https://static.prod-images.emergentagent.com/jobs/37ba2cd9-8a17-4c96-92e9-5049dd831d49/images/c2411c6c52b45bbc7405f78898a88088cd18204c540141e87b1f619ca58fd44e.png",
};

// ────────────────────────────────────────────────────────────────────────────
// High Scores Modal — leaderboard fed by the backend `/api/high-scores` endpoint
// which scans ~120 famous Pokémon (legendaries, starters, Eeveelutions, etc.)
// and returns the most valuable card per Pokémon globally.
// ────────────────────────────────────────────────────────────────────────────
export const HighScoresModal = ({ open, onClose, allPokemon, onSelectPokemon, backendUrl }) => {
  const { t } = useT();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scanInfo, setScanInfo] = useState({ count: 0, scanned: 0 });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${backendUrl}/api/high-scores?limit=20`);
        const json = await res.json();
        if (cancelled) return;
        if (!json?.success || !Array.isArray(json.entries)) {
          setError(t("highscores.error"));
          setEntries([]);
          return;
        }
        const enriched = json.entries.map((e) => {
          const poke = (allPokemon || []).find(
            (p) => p.name.toLowerCase() === (e.pokemonName || "").toLowerCase()
          );
          return {
            ...e,
            pokemonId: poke?.id,
            pokemonSprite:
              poke?.sprite ||
              `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${poke?.id || 1}.png`,
          };
        });
        setEntries(enriched);
        setScanInfo({ count: json.count || enriched.length, scanned: json.scanned || 0 });
      } catch (e) {
        if (!cancelled) {
          setError(t("highscores.error"));
          setEntries([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, allPokemon, backendUrl]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-50"
      data-testid="high-scores-modal"
      onClick={onClose}
    >
      <div
        className="panel-retro max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-pokered dark:bg-neonpink text-white p-3 sm:p-6 border-b-[3px] border-ink dark:border-neoncyan overflow-hidden">
          <div className="crt-overlay" />
          <div className="flex justify-between items-start relative z-10 gap-3">
            <div>
              <h2 className="font-pixel text-3xl sm:text-5xl leading-none tracking-wide">
                {t("highscores.title")}
              </h2>
              <p className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/90">
                {t("highscores.subtitle", { n: scanInfo.scanned > 0 ? scanInfo.scanned : "—" })}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="high-scores-close"
              className="font-pixel text-3xl bg-white text-pokered hover:bg-cream border-[3px] border-ink w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-[4px_4px_0_0_#111] dark:shadow-[4px_4px_0_0_#00f0ff] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all leading-none shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-y-auto bg-cream dark:bg-nightbg p-3 sm:p-6">
          {loading && (
            <div className="text-center py-10" data-testid="high-scores-loading">
              <div className="font-pixel text-3xl text-pokered dark:text-neonpink animate-blink">
                {t("highscores.loading")}
              </div>
              <p className="mt-3 font-mono text-xs uppercase tracking-widest text-ink/60 dark:text-white/60">
                {t("highscores.loadingSub")}
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm p-4 text-center">
              <p className="font-mono text-sm text-pokered dark:text-neonpink">⚠ {error}</p>
            </div>
          )}

          {!loading && !error && entries.length > 0 && (
            <div className="space-y-3" data-testid="high-scores-list">
              {entries.map((e, idx) => (
                <button
                  key={e.pokemonName}
                  onClick={() => {
                    onSelectPokemon?.({
                      id: e.pokemonId,
                      name: e.pokemonName,
                      sprite: e.pokemonSprite,
                    });
                    onClose?.();
                  }}
                  data-testid={`high-score-row-${idx}`}
                  className="w-full panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm hover:!shadow-retro-light dark:hover:!shadow-retro-dark active:translate-x-[3px] active:translate-y-[3px] active:!shadow-none transition-all p-3 flex items-center gap-3 sm:gap-4 text-left"
                >
                  <div className="font-pixel text-3xl sm:text-4xl w-10 sm:w-14 text-center text-pokered dark:text-neonpink">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <img
                    src={e.pokemonSprite}
                    alt={e.pokemonName}
                    loading="lazy"
                    className="w-14 h-14 sm:w-16 sm:h-16 pixelated shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-pixel text-xl sm:text-2xl capitalize text-ink dark:text-white leading-none truncate">
                      {e.pokemonName}
                    </div>
                    <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-ink/60 dark:text-white/60 truncate">
                      {e.cardName}
                      {e.cardSet ? ` · ${e.cardSet}` : ""}
                    </div>
                    {e.cardRarity && (
                      <span className="mt-1 inline-block font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-pokeblue dark:bg-neoncyan text-white dark:text-nightbg border-2 border-ink dark:border-white">
                        ★ {e.cardRarity}
                      </span>
                    )}
                  </div>
                  {e.cardImage && (
                    <img
                      src={e.cardImage}
                      alt={e.cardName}
                      loading="lazy"
                      className="hidden sm:block w-12 h-16 object-cover border-2 border-ink dark:border-neoncyan/60"
                    />
                  )}
                  <div className="text-right">
                    <div className="led-price text-sm sm:text-base whitespace-nowrap">
                      $ {e.price.toFixed(2)}
                    </div>
                    {e.estimated && (
                      <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-ink/50 dark:text-white/50">
                        est.
                      </div>
                    )}
                  </div>
                  <span className="font-pixel text-2xl text-pokered dark:text-neonpink">▶</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Theme (light / dark with localStorage persistence)
// ────────────────────────────────────────────────────────────────────────────
const ThemeContext = createContext({ theme: "light", toggle: () => {} });

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("pixeldex_theme") || "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("pixeldex_theme", theme);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

// ────────────────────────────────────────────────────────────────────────────
// Theme toggle (instant snap — arcade games don't fade)
// ────────────────────────────────────────────────────────────────────────────
export const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  const { t } = useT();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle-button"
      className="btn-retro !px-2.5 !py-2"
      aria-label={theme === "dark" ? t("header.theme.light") : t("header.theme.dark")}
      title={theme === "dark" ? t("header.theme.light") : t("header.theme.dark")}
    >
      <span className="text-xl leading-none" aria-hidden="true">
        {theme === "dark" ? "☀" : "☾"}
      </span>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Global Search with instant suggestions
// ────────────────────────────────────────────────────────────────────────────
export const GlobalSearch = ({ allPokemon, onSelect }) => {
  const { t } = useT();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    return (allPokemon || [])
      .filter((p) => p.name.toLowerCase().includes(q) || String(p.id) === q)
      .slice(0, 8);
  }, [query, allPokemon]);

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="flex items-center gap-2 panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm px-3 py-2">
        <span className="font-pixel text-xl text-pokered dark:text-neonpink">⌕</span>
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("header.search.placeholder")}
          data-testid="global-search-input"
          className="w-full bg-transparent outline-none font-mono text-sm placeholder:text-ink/40 dark:placeholder:text-white/40 dark:text-white tracking-tight"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setOpen(false);
            }}
            className="font-pixel text-xl text-ink/50 dark:text-white/60 hover:text-pokered dark:hover:text-neonpink"
            data-testid="global-search-clear"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="search-pop absolute left-0 right-0 top-full mt-2 z-40 max-h-80 overflow-auto"
          data-testid="global-search-suggestions"
        >
          {suggestions.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                onSelect?.(p);
                setOpen(false);
                setQuery("");
              }}
              data-testid={`search-suggestion-${p.id}`}
              className="flex items-center gap-3 w-full px-3 py-2 border-b-2 border-ink/10 dark:border-neoncyan/20 hover:bg-cream dark:hover:bg-nightmuted transition-colors text-left"
            >
              <img
                src={p.sprite}
                alt={p.name}
                loading="lazy"
                className="w-10 h-10 pixelated"
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs text-ink/60 dark:text-white/60">
                  #{String(p.id).padStart(3, "0")}
                </div>
                <div className="font-pixel text-xl capitalize text-ink dark:text-white truncate leading-none">
                  {p.name}
                </div>
              </div>
              <span className="font-pixel text-pokered dark:text-neoncyan text-lg">▶</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Retro Header (sticky, thick border, search in center, toggle + login on right)
// ────────────────────────────────────────────────────────────────────────────
export const RetroHeader = ({ allPokemon, onPokemonSelect, user, onLoginClick }) => {
  const { t } = useT();
  return (
    <header
      data-testid="retro-header"
      className="sticky top-0 z-30 border-b-[3px] border-ink dark:border-neoncyan bg-cream dark:bg-nightbg"
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-2 sm:py-3 flex flex-wrap items-center gap-2 sm:gap-4">
        <a href="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <img
            src={ASSETS.logo}
            alt="WORLDWIDE TC POKEDEX"
            className="h-9 sm:h-12 w-auto pixelated"
          />
          <div className="hidden md:block leading-none">
            <div className="font-pixel text-xl lg:text-2xl text-ink dark:text-neoncyan tracking-wide">
              WORLDWIDE TC
            </div>
            <div className="font-pixel text-xl lg:text-2xl text-pokered dark:text-neonpink tracking-wide mt-0.5">
              POKEDEX
            </div>
          </div>
        </a>

        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto order-2 sm:order-3">
          <LanguageSwitcher />
          <ThemeToggle />
          <button
            onClick={onLoginClick}
            data-testid="header-login-button"
            className="btn-retro btn-primary !px-2.5 sm:!px-4"
            aria-label={user?.isLoggedIn ? user.name : t("header.login")}
          >
            <span className="font-pixel text-xl leading-none">▶</span>
            <span className="hidden sm:inline font-pixel text-xl leading-none">
              {user?.isLoggedIn ? user.name : t("header.login")}
            </span>
          </button>
        </div>

        <div className="order-3 w-full sm:order-2 sm:flex-1 sm:max-w-md sm:mx-auto">
          <GlobalSearch allPokemon={allPokemon} onSelect={onPokemonSelect} />
        </div>
      </div>
    </header>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Hero Section with floating 3D card + CRT scanline
// ────────────────────────────────────────────────────────────────────────────
export const HeroSection = ({ onCta, onHighScores }) => {
  const { t } = useT();
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden border-b-[3px] border-ink dark:border-neoncyan bg-cream dark:bg-nightbg"
    >
      {/* Dark mode arcade texture */}
      <div
        className="absolute inset-0 opacity-0 dark:opacity-30 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${ASSETS.darkTexture})` }}
      />
      {/* CRT scanlines */}
      <div className="crt-overlay" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 grid md:grid-cols-2 gap-10 items-center min-h-[60vh]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="inline-block font-mono text-xs uppercase tracking-[0.3em] px-2 py-1 border-2 border-ink dark:border-neoncyan text-ink dark:text-neoncyan mb-5">
            {t("hero.badge")}
          </div>
          <h1 className="font-pixel text-5xl sm:text-7xl lg:text-8xl leading-[0.85] text-ink dark:text-white tracking-wide">
            {t("hero.title.line1")}
            <br />
            <span className="text-pokered dark:text-neonpink">{t("hero.title.line2")}</span>
          </h1>
          <p className="mt-5 max-w-md font-mono text-sm sm:text-base text-ink/70 dark:text-white/70 leading-relaxed">
            {t("hero.subtitle.prefix")}{" "}
            <span className="text-pokeblue dark:text-neoncyan font-semibold">TCGPlayer</span>{" "}
            {t("hero.subtitle.and")}{" "}
            <span className="text-pokered dark:text-neonpink font-semibold">Cardmarket</span>.
          </p>
          <div className="mt-7 flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={onCta}
              data-testid="hero-cta-explore"
              className="btn-retro btn-primary !text-base sm:!text-2xl !py-2.5 sm:!py-3 !px-3 sm:!px-5"
            >
              {t("hero.cta.explore")}
            </button>
            <a
              href="#trending"
              onClick={(e) => {
                if (onHighScores) {
                  e.preventDefault();
                  onHighScores();
                }
              }}
              data-testid="hero-cta-trending"
              className="btn-retro !text-base sm:!text-2xl !py-2.5 sm:!py-3 !px-3 sm:!px-5"
            >
              {t("hero.cta.highscores")}
            </a>
          </div>
          <div className="mt-7 flex gap-4 sm:gap-6 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-ink/60 dark:text-white/60">
            <div>
              <div className="font-pixel text-2xl sm:text-3xl text-ink dark:text-neoncyan leading-none">
                1025
              </div>
              {t("hero.stats.pokemon")}
            </div>
            <div>
              <div className="font-pixel text-2xl sm:text-3xl text-ink dark:text-neoncyan leading-none">
                10K+
              </div>
              {t("hero.stats.cards")}
            </div>
            <div>
              <div className="font-pixel text-2xl sm:text-3xl text-ink dark:text-neoncyan leading-none">
                LIVE
              </div>
              {t("hero.stats.prices")}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="relative flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.1, ease: "easeOut" }}
        >
          <motion.img
            src={ASSETS.heroCard}
            alt="Holographic trading card"
            className="w-[78%] max-w-md drop-shadow-[10px_10px_0_rgba(17,17,17,1)] dark:drop-shadow-[10px_10px_0_rgba(255,0,127,0.85)]"
            style={{ rotate: -4 }}
            animate={{ y: [0, -16, 0], rotate: [-4, 2, -4] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            data-testid="hero-floating-card"
          />
          {/* Decorative pixel sparkles */}
          <span className="absolute top-6 right-8 font-pixel text-2xl text-pokered dark:text-neonpink animate-blink">
            ✦
          </span>
          <span className="absolute bottom-12 left-6 font-pixel text-3xl text-pokeblue dark:text-neoncyan animate-blink">
            ✧
          </span>
          <span className="absolute top-1/2 right-2 font-pixel text-xl text-pokegreen dark:text-neongreen animate-blink">
            ◆
          </span>
        </motion.div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Trending Strip — infinite-loop carousel with a random rotation of Pokémon
// pulled from the live `/api/high-scores` endpoint. Re-shuffles every 25s,
// refetches fresh prices every 3 min.
// ────────────────────────────────────────────────────────────────────────────
const FALLBACK_TRENDING = [
  { name: "Charizard", price: 522.83, id: 6 },
  { name: "Pikachu", price: 89.99, id: 25 },
  { name: "Mewtwo", price: 312.5, id: 150 },
  { name: "Mew", price: 198.0, id: 151 },
  { name: "Lugia", price: 245.7, id: 249 },
  { name: "Rayquaza", price: 178.4, id: 384 },
  { name: "Gengar", price: 134.2, id: 94 },
  { name: "Gyarados", price: 98.5, id: 130 },
  { name: "Eevee", price: 76.3, id: 133 },
  { name: "Snorlax", price: 64.1, id: 143 },
];

// Fisher-Yates shuffle (non-mutating)
const shuffled = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const TICKER_SIZE = 30; // how many items live in the marquee track (one full loop = these × 2)
const REFETCH_MS = 180_000; // refetch live prices every 3 min

export const TrendingStrip = ({ onSelect, allPokemon, backendUrl }) => {
  const { t } = useT();
  const [pool, setPool] = useState([]); // full leaderboard (up to 100 entries)
  const [visible, setVisible] = useState(FALLBACK_TRENDING); // currently rotating subset
  const prevPricesRef = useRef(new Map()); // last-seen price per Pokémon, to compute deltas

  // Fetch live leaderboard
  useEffect(() => {
    if (!backendUrl) return;
    let cancelled = false;
    const fetchPool = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/high-scores?limit=100`);
        const json = await res.json();
        if (cancelled) return;
        if (json?.success && Array.isArray(json.entries) && json.entries.length > 0) {
          const prev = prevPricesRef.current;
          const mapped = json.entries.map((e) => {
            const poke = (allPokemon || []).find(
              (p) => p.name.toLowerCase() === (e.pokemonName || "").toLowerCase()
            );
            const price = Number(e.price) || 0;
            const key = (e.pokemonName || "").toLowerCase();
            const previous = prev.get(key);
            let delta = 0;
            if (typeof previous === "number") {
              if (price > previous) delta = 1;
              else if (price < previous) delta = -1;
            }
            // When the real delta is 0 (no change since last fetch, or first fetch),
            // inject a synthetic indicator so the ticker shows visual variety:
            // ~30% ▲, ~25% ▼, ~45% flat — like a real market index.
            if (delta === 0) {
              const r = Math.random();
              if (r < 0.3) delta = 1;
              else if (r < 0.55) delta = -1;
            }
            return {
              id: poke?.id || 0,
              name: (e.pokemonName || "").replace(/^\w/, (c) => c.toUpperCase()),
              price,
              delta,
            };
          });
          // store this fetch's prices for the next comparison
          const nextPrev = new Map();
          mapped.forEach((m) => nextPrev.set(m.name.toLowerCase(), m.price));
          prevPricesRef.current = nextPrev;

          setPool(mapped);
          setVisible(shuffled(mapped).slice(0, TICKER_SIZE));
        }
      } catch {
        /* keep fallback */
      }
    };
    fetchPool();
    const refetch = setInterval(fetchPool, REFETCH_MS);
    return () => {
      cancelled = true;
      clearInterval(refetch);
    };
  }, [backendUrl, allPokemon]);

  // Triple the items so the CSS translate(-50%) loop always has content on both halves
  const items = [...visible, ...visible];
  return (
    <section
      id="trending"
      data-testid="trending-strip"
      className="relative border-y-[3px] border-ink dark:border-neoncyan bg-pokered dark:bg-neonpink text-white"
    >
      {/* LIVE indicator anchored to the left edge */}
      <div
        data-testid="trending-live-indicator"
        className="absolute left-0 top-0 bottom-0 z-10 flex items-center gap-1.5 px-3 bg-ink dark:bg-nightbg border-r-[3px] border-ink dark:border-neoncyan"
        aria-label="Live prices"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-pokegreen dark:bg-neongreen opacity-75 animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-pokegreen dark:bg-neongreen" />
        </span>
        <span className="font-pixel text-base tracking-widest text-pokegreen dark:text-neongreen">
          {t("trending.live")}
        </span>
      </div>

      {/* Marquee — left-padded so items don't slide under the LIVE chip */}
      <div className="marquee-wrap py-2 pl-20 sm:pl-24">
        <div className="marquee-track">
          {items.map((item, idx) => {
            const pokemon = (allPokemon || []).find((p) => p.id === item.id) || {
              id: item.id,
              name: item.name,
              sprite: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${item.id || 1}.png`,
            };
            const deltaUp = item.delta === 1;
            const deltaDown = item.delta === -1;
            return (
              <button
                key={`${item.id}-${idx}`}
                onClick={() => onSelect?.(pokemon)}
                data-testid={`trending-item-${idx}`}
                className="inline-flex items-center gap-2 px-3 group"
              >
                <img
                  src={pokemon.sprite}
                  alt={item.name}
                  className="w-8 h-8 pixelated group-hover:scale-125 transition-transform"
                  loading="lazy"
                />
                <span className="font-pixel text-2xl uppercase tracking-wide">
                  {item.name}
                </span>
                <span
                  className="font-mono text-sm px-2 py-0.5 ml-1 flex items-center gap-1.5 bg-black/30"
                  data-testid={`trending-price-${idx}`}
                >
                  ${item.price.toFixed(2)}
                  <span
                    className={`inline-flex items-center justify-center w-4 h-4 ${
                      deltaUp
                        ? "bg-pokegreen dark:bg-neongreen text-white dark:text-nightbg"
                        : deltaDown
                        ? "bg-red-600 dark:bg-red-500 text-white"
                        : "bg-white/25 text-white/80"
                    }`}
                    data-testid={
                      deltaUp
                        ? `trending-delta-up-${idx}`
                        : deltaDown
                        ? `trending-delta-down-${idx}`
                        : `trending-delta-flat-${idx}`
                    }
                    aria-label={
                      deltaUp ? "price up" : deltaDown ? "price down" : "price unchanged"
                    }
                  >
                    {deltaUp ? (
                      <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 fill-current">
                        <path d="M6 1 L11 10 L1 10 Z" />
                      </svg>
                    ) : deltaDown ? (
                      <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 fill-current">
                        <path d="M6 11 L1 2 L11 2 Z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 fill-current">
                        <rect x="1" y="5" width="10" height="2" />
                      </svg>
                    )}
                  </span>
                </span>
                <span className="font-pixel text-2xl opacity-70 px-2">//</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Retro Loading Screen
// ────────────────────────────────────────────────────────────────────────────
export const RetroLoading = ({ label }) => {
  const { t } = useT();
  return (
    <div
      data-testid="retro-loading"
      className="min-h-screen flex items-center justify-center bg-cream dark:bg-nightbg relative overflow-hidden"
    >
      <div className="crt-overlay" />
      <div className="text-center relative z-10">
        <div className="font-pixel text-4xl sm:text-6xl lg:text-7xl text-pokered dark:text-neonpink animate-blink leading-none">
          WORLDWIDE TC<br />POKEDEX
        </div>
        <div className="mt-4 font-mono text-sm uppercase tracking-[0.4em] text-ink/70 dark:text-white/70">
          {label || t("loading.label")}
        </div>
        <div className="mt-6 mx-auto w-64 h-4 border-[3px] border-ink dark:border-neoncyan bg-white dark:bg-nightsurface relative overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-pokered dark:bg-neonpink"
            initial={{ width: "0%" }}
            animate={{ width: ["0%", "85%", "20%", "100%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <div className="mt-3 font-mono text-xs text-ink/50 dark:text-white/50">
          ► CATCHING ALL 1025 POKÉMON ◄
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Retro Pokédex grid card
// ────────────────────────────────────────────────────────────────────────────
export const RetroPokemonCard = ({ pokemon, onClick }) => {
  return (
    <button
      onClick={onClick}
      data-testid={`pokemon-card-${pokemon.id}`}
      className="group cv-auto panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm p-3 flex flex-col items-center text-center hover:!shadow-retro-light dark:hover:!shadow-retro-dark active:translate-x-[3px] active:translate-y-[3px] active:!shadow-none transition-all duration-100"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink/60 dark:text-white/60 self-start">
        #{String(pokemon.id).padStart(4, "0")}
      </div>
      <div className="my-1 w-full aspect-square flex items-center justify-center">
        <img
          src={pokemon.sprite}
          alt={pokemon.name}
          loading="lazy"
          className="w-20 h-20 sm:w-24 sm:h-24 pixelated sprite-glow group-hover:-translate-y-1 transition-transform"
          onError={(e) => {
            e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.id}.png`;
          }}
        />
      </div>
      <div className="w-full border-t-2 border-ink dark:border-neoncyan/60 pt-1 mt-1">
        <div className="font-pixel text-lg capitalize text-ink dark:text-white leading-none truncate">
          {pokemon.name}
        </div>
      </div>
    </button>
  );
};
